import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ChevronDown, Loader2, ShieldAlert, Target } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { runAiJob } from '../../lib/aiJob';
import { VAL_COLOR, VAL_LABEL, OUT_LABEL } from '../../lib/evaluation';

// Cerebro's victory strategy for the chosen case, shown above the Fidem chat
export const FidemStrategyStrip = ({ matterId }) => {
  const { api } = useAuth();
  const [m, setM] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setM(null);
    if (matterId) api.get(`/crm/matters/${matterId}`).then((r) => setM(r.data.matter)).catch(() => {});
  }, [matterId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!matterId || !m) return null;
  const st = m.ai_strategy;
  const ev = m.latest_evaluation;
  const generate = async () => {
    setBusy(true);
    try { const r = await runAiJob(api, `/crm/matters/${matterId}/strategy`, {}, { timeout: 300000 }); setM({ ...m, ai_strategy: r.strategy, ai_win_probability: r.strategy.probabilidad_victoria }); setOpen(true); } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const badge = <span data-testid="fidem-strategy-prob" title={ev ? `${ev.metodo} · incertidumbre ${ev.incertidumbre}` : 'Sin método calibrado para porcentajes'} className={`shrink-0 rounded-full border border-slate-700 px-2.5 py-0.5 text-xs font-bold ${ev ? VAL_COLOR[ev.valoracion] : 'text-slate-400'}`}>{ev ? `${VAL_LABEL[ev.valoracion]} · ${OUT_LABEL[ev.resultado]} · ${ev.created_at.slice(0, 10)}` : 'Sin valoración del abogado'}</span>;
  if (!st) return (
    <div data-testid="fidem-strategy-strip" className="mt-3 flex flex-wrap items-center gap-3 rounded-xl border border-slate-800 bg-slate-900/60 px-4 py-2 text-xs text-slate-400">
      {badge}<Target className="h-4 w-4 text-amber-400" /> Cerebro aún no tiene la estrategia de victoria de esta causa.
      <button data-testid="fidem-strategy-generate" disabled={busy} onClick={generate} className="ml-auto flex items-center gap-1 rounded-full bg-amber-500 px-3 py-1 font-semibold text-slate-950 disabled:opacity-60">{busy && <Loader2 className="h-3 w-3 animate-spin" />}{busy ? 'Calculando (1-2 min)…' : 'Calcular estrategia'}</button>
    </div>
  );
  return (
    <div data-testid="fidem-strategy-strip" className="mt-3 rounded-xl border border-slate-800 bg-slate-900/60">
      <button data-testid="fidem-strategy-toggle" onClick={() => setOpen(!open)} className="flex w-full items-center gap-3 px-4 py-2 text-left">
        {badge}
        <span className="shrink-0 text-xs font-semibold text-amber-200">Estrategia de victoria</span>
        <span data-testid="fidem-strategy-summary" className={`min-w-0 flex-1 truncate text-xs text-slate-300 ${open ? 'invisible' : ''}`}>{st.resumen}</span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <p data-testid="fidem-strategy-full" className="px-4 pb-3 text-xs leading-relaxed text-slate-300">{st.resumen}</p>}
      {open && <StrategyRisks risks={st.riesgos} />}
    </div>
  );
};

const StrategyRisks = ({ risks = [] }) => (
  <div data-testid="fidem-strategy-risks" className="grid gap-2 border-t border-slate-800 px-4 py-3 sm:grid-cols-3">
    {risks.slice(0, 3).map((r, i) => (
      <div key={i} className="rounded-lg bg-slate-950/60 p-2 text-xs">
        <p className="flex items-start gap-1 font-medium text-rose-200"><ShieldAlert className="mt-0.5 h-3 w-3 shrink-0" />{r.riesgo || r}</p>
        {r.mitigacion && <p className="mt-1 text-slate-400">{r.mitigacion}</p>}
      </div>
    ))}
    {!risks.length && <p className="text-xs text-slate-500">Cerebro no registró riesgos para esta causa.</p>}
  </div>
);
